import React from 'react';

// Shared look for the student pages. Every value reads from the active
// theme's CSS variables (set by StudentApp via themeToCssVars), so one
// component looks right on all themes, light or dark, without per-theme code.

// Card colours live in classes (not inline styles) so hover states can override them.
export const ST_CARD = 'rounded-[var(--st-radius-card)] border bg-[var(--st-card)] border-[var(--st-border)] shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
// A card you can click: lifts its border to the theme colour on hover and focus.
export const ST_CARD_LINK = `${ST_CARD} transition-[border-color,box-shadow] hover:border-[color-mix(in_srgb,var(--st-primary)_45%,var(--st-border))] hover:shadow-[0_4px_16px_-6px_color-mix(in_srgb,var(--st-primary)_35%,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-primary)]`;

export const ST_BTN = 'h-10 px-4 rounded-[var(--st-radius-pill)] text-sm font-medium inline-flex items-center justify-center gap-2 transition-[opacity,background-color,border-color,filter] hover:brightness-105 disabled:opacity-50';
export const stPrimaryStyle = { background: 'var(--st-primary)', color: 'var(--st-on-primary)' };
export const stSecondaryStyle = { border: '1px solid var(--st-border)', background: 'var(--st-card)', color: 'var(--st-ink)' };

// Soft tinted tiles behind icons and small figures, inked so they read on every theme.
export const TINT_PRIMARY = { background: 'color-mix(in srgb, var(--st-primary) 14%, var(--st-card))', color: 'var(--st-primary-ink)' };
export const TINT_ACCENT = { background: 'color-mix(in srgb, var(--st-accent) 16%, var(--st-card))', color: 'var(--st-accent-ink)' };

/** Page title in the theme's heading font, with an optional one-line description and action. */
export function StPageHeading({ title, description, action }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <h1 className="text-3xl font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>{title}</h1>
        <AccentRule className="mt-2" />
        {description && <p className="mt-3 text-sm max-w-3xl" style={{ color: 'var(--st-mute)' }}>{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** The student side's one signature mark: a short stroke in the theme's accent under a title. */
export function AccentRule({ className = '' }) {
  return (
    <span aria-hidden="true" className={`block h-1 w-10 rounded-full ${className}`}
      style={{ background: 'var(--st-accent)' }} />
  );
}

/** Section title: a sentence-case heading with a quiet count beside it. */
export function SectionTitle({ title, count, aside }) {
  return (
    <div className="mb-4 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--st-ink)' }}>{title}</h2>
          {typeof count === 'number' && (
            <span className="text-xs font-medium tabular-nums px-2 py-0.5 rounded-full" style={TINT_PRIMARY}>{count}</span>
          )}
        </div>
        <AccentRule className="mt-2" />
      </div>
      {aside}
    </div>
  );
}

export function EmptyNote({ children, testId }) {
  return (
    <div className={`${ST_CARD} border-dashed shadow-none p-6 text-sm text-center`} style={{ color: 'var(--st-mute)' }} data-testid={testId}>
      {children}
    </div>
  );
}
